import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, TextField, MenuItem, Button, Alert,
  Chip, Checkbox,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  Search as SearchIcon, CheckCircle as CheckIcon, Done as DoneIcon,
} from '@mui/icons-material';
import { useConciliacionStore } from '../store/conciliacionStore';
import { useCajaBancoStore } from '../../cajas-bancos/store/cajaBancoStore';

const MESES = [
  { value: '01', label: 'Enero' },
  { value: '02', label: 'Febrero' },
  { value: '03', label: 'Marzo' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Mayo' },
  { value: '06', label: 'Junio' },
  { value: '07', label: 'Julio' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

const ConciliacionBancariaPage: React.FC = () => {
  const [idCajaBanco, setIdCajaBanco] = useState<number>(0);
  const [ejercicio, setEjercicio] = useState(new Date().getFullYear().toString());
  const [periodo, setPeriodo] = useState(
    String(new Date().getMonth() + 1).padStart(2, '0'),
  );

  const {
    conciliacion, loading, error, selectedIds,
    fetchConciliacion, conciliar, aplicar,
    toggleSelected, selectAll, clearSelection, clearError,
  } = useConciliacionStore();
  const { cajasBancos, fetchCajasBancos } = useCajaBancoStore();

  useEffect(() => {
    fetchCajasBancos();
  }, [fetchCajasBancos]);

  const handleConsultar = () => {
    if (idCajaBanco) {
      fetchConciliacion(idCajaBanco, ejercicio, periodo);
    }
  };

  const handleConciliar = async () => {
    if (selectedIds.length === 0) return;
    try {
      await conciliar({
        idCajaBanco,
        ejercicio,
        periodo,
        movimientoIds: selectedIds,
        fechaConciliacion: new Date().toISOString(),
      });
    } catch {
      /* store handles */
    }
  };

  const handleAplicar = async () => {
    if (conciliacion) {
      try {
        await aplicar(conciliacion.idConciliacionBancaria);
      } catch {
        /* store handles */
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'select',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <Checkbox
          size="small"
          checked={
            selectedIds.includes(params.row.idMovimientoTesoreria) ||
            params.row.esConciliado
          }
          disabled={params.row.esConciliado}
          onChange={() => toggleSelected(params.row.idMovimientoTesoreria)}
        />
      ),
    },
    { field: 'numeroMovimiento', headerName: 'Numero', width: 130 },
    {
      field: 'fechaMovimiento',
      headerName: 'Fecha',
      width: 110,
      valueFormatter: (value: string) =>
        value ? new Date(value).toLocaleDateString('es-PE') : '',
    },
    { field: 'glosa', headerName: 'Descripcion', flex: 1, minWidth: 200 },
    {
      field: 'debe',
      headerName: 'Debe',
      width: 130,
      type: 'number',
      valueFormatter: (value: number) =>
        value?.toLocaleString('es-PE', { minimumFractionDigits: 2 }),
    },
    {
      field: 'haber',
      headerName: 'Haber',
      width: 130,
      type: 'number',
      valueFormatter: (value: number) =>
        value?.toLocaleString('es-PE', { minimumFractionDigits: 2 }),
    },
    {
      field: 'esConciliado',
      headerName: 'Conciliado',
      width: 110,
      renderCell: (params) =>
        params.value ? (
          <Chip label="Si" size="small" color="info" icon={<CheckIcon />} />
        ) : (
          <Chip label="No" size="small" variant="outlined" />
        ),
    },
    {
      field: 'saldoContable',
      headerName: 'Saldo Contable',
      width: 140,
      type: 'number',
      valueFormatter: (value: number) =>
        value?.toLocaleString('es-PE', { minimumFractionDigits: 2 }),
    },
    {
      field: 'saldoBancario',
      headerName: 'Saldo Bancario',
      width: 140,
      type: 'number',
      valueFormatter: (value: number) =>
        value?.toLocaleString('es-PE', { minimumFractionDigits: 2 }),
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        Conciliacion Bancaria
      </Typography>

      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Caja/Banco"
            select
            size="small"
            sx={{ minWidth: 200 }}
            value={idCajaBanco || ''}
            onChange={(e) => setIdCajaBanco(parseInt(e.target.value) || 0)}
          >
            <MenuItem value="">Seleccionar...</MenuItem>
            {cajasBancos.map((cb) => (
              <MenuItem key={cb.idCajaBanco} value={cb.idCajaBanco}>
                {cb.nombre}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Ejercicio"
            size="small"
            value={ejercicio}
            onChange={(e) => setEjercicio(e.target.value)}
            sx={{ width: 100 }}
          />
          <TextField
            label="Periodo"
            select
            size="small"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            sx={{ width: 150 }}
          >
            {MESES.map((m) => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleConsultar}
            disabled={loading || !idCajaBanco}
          >
            Consultar
          </Button>
          {conciliacion && (
            <>
              <Button variant="outlined" onClick={selectAll}>
                Seleccionar Todos
              </Button>
              <Button variant="outlined" onClick={clearSelection}>
                Limpiar Seleccion
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckIcon />}
                onClick={handleConciliar}
                disabled={selectedIds.length === 0}
              >
                Conciliar ({selectedIds.length})
              </Button>
              {conciliacion.estado === 'PENDIENTE' && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DoneIcon />}
                  onClick={handleAplicar}
                >
                  Aplicar
                </Button>
              )}
            </>
          )}
        </Box>
      </Paper>

      <Paper sx={{ height: 'calc(100vh - 350px)' }}>
        <DataGrid
          rows={conciliacion?.detalles || []}
          columns={columns}
          getRowId={(row) => row.idConciliacionBancariaDetalle}
          loading={loading}
          pageSizeOptions={[50, 100]}
          disableRowSelectionOnClick
          getRowClassName={(params) =>
            params.row.esConciliado ? 'row-conciliado' : ''
          }
          sx={{
            '& .row-conciliado': {
              bgcolor: 'action.selected',
              color: 'info.main',
            },
          }}
        />
      </Paper>

      {conciliacion?.resumen && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Resumen de Conciliacion
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 1,
            }}
          >
            <Typography>
              Saldo Contable Anterior:{' '}
              {conciliacion.resumen.saldoContableAnterior.toLocaleString('es-PE', {
                minimumFractionDigits: 2,
              })}
            </Typography>
            <Typography>
              Total Contable:{' '}
              {conciliacion.resumen.totalContable.toLocaleString('es-PE', {
                minimumFractionDigits: 2,
              })}
            </Typography>
            <Typography>
              Total Conciliado:{' '}
              {conciliacion.resumen.totalConciliado.toLocaleString('es-PE', {
                minimumFractionDigits: 2,
              })}
            </Typography>
            <Typography>
              Total Pendiente:{' '}
              {conciliacion.resumen.totalPendiente.toLocaleString('es-PE', {
                minimumFractionDigits: 2,
              })}
            </Typography>
            <Typography fontWeight="bold">
              Saldo Bancario:{' '}
              {conciliacion.resumen.saldoBancario.toLocaleString('es-PE', {
                minimumFractionDigits: 2,
              })}
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ConciliacionBancariaPage;
